import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, resolveDirective as _resolveDirective, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, vShow as _vShow, unref as _unref } from "vue";
const _hoisted_1 = { class: "card-title d-flex align-items-center" };
const _hoisted_2 = /*#__PURE__*/ _createElementVNode("i", { class: "icon icon-table-30px" }, null, -1);
const _hoisted_3 = { class: "m-0" };
const _hoisted_4 = {
    key: 0,
    class: "el-table__column"
};
const _hoisted_5 = /*#__PURE__*/ _createElementVNode("i", { class: "icon icon-question-14px" }, null, -1);
const _hoisted_6 = {
    key: 1,
    class: "el-table__column"
};
const _hoisted_7 = {
    key: 0,
    class: "el-table__column"
};
const _hoisted_8 = /*#__PURE__*/ _createElementVNode("i", { class: "icon icon-question-14px" }, null, -1);
const _hoisted_9 = {
    key: 1,
    class: "el-table__column"
};
const _hoisted_10 = { class: "engagement-report-table__actions" };
import { useStore } from 'vuex';
import { ref, computed, watch } from 'vue';
import i18n from '@/i18n';
import { Close, Search } from '@element-plus/icons-vue';
// components
import EngagementReportTableOptions from './EngagementReportTableOptions/TableOptions.vue';
import EngagementReportEditColumns from './EngagementReportEditColumns/EditColumns.vue';
import BaseLoader from '@/components/_atoms/BaseLoaderSpinner.vue';
import InfoCard from '@/components/_atoms/InfoCard.vue';
import ListPagination from '@/components/_atoms/ListPagination.vue';
// utils
import useTableMixin from '@/utilities/mixins/useTableMixin';
import { useMixpanelSortedColumn } from '@/utilities/mixpanel';
export default /*#__PURE__*/ _defineComponent({
    __name: 'Table',
    setup(__props) {
        // libs
        const { getCheckThreshold, useGetValue } = useTableMixin();
        const store = useStore();
        // state
        const tableFiltered = ref([]);
        const columnsFiltered = ref([]);
        const searchTerm = ref({});
        const noMask = ref({
            mask: '*'.repeat(255),
            tokens: {
                '*': { pattern: /./ },
            },
        });
        const sort = ref({
            prop: '',
            order: '',
            orderBy: '',
        });
        // actions
        const fetchTable = () => store.dispatch('engagementReport/setFetchTable');
        const setIsTableExpanded = (isExpanded) => store.dispatch('engagementReport/setIsTableExpanded', isExpanded);
        const updateByDatepicker = () => store.dispatch('engagementReport/updateTableByDatepicker');
        const updateOrderBy = (orderBy) => store.dispatch('engagementReport/setOrderBy', orderBy);
        const setQuery = ({ column, value }) => store.dispatch('engagementReport/setQuery', { column, value });
        const clearQuery = (column) => store.dispatch('engagementReport/setClearSearch', column);
        // computed
        const getHasErrorTable = computed(() => store.getters['engagementReport/getHasError']);
        const getIsEmptyTable = computed(() => store.getters['engagementReport/getIsEmpty']);
        const getTableHeader = computed(() => store.getters['engagementReport/getTableHeader']);
        const getTableBody = computed(() => store.getters['engagementReport/getTableBody']);
        const getTableSummaryRow = computed(() => store.getters['engagementReport/getTableSummaryRow']);
        const getIsTableLoading = computed(() => store.getters['engagementReport/getIsTableLoading']);
        const getColumnsToSearch = computed(() => {
            return {
                no_label: getTableHeader.value.filter(column => !column.isMetric).map(c => c.key),
                with_label: getTableHeader.value.filter(column => !column.isMetric).map(c => ({ key: c.key, label: c.name })),
            };
        });
        const getIsTableExpanded = computed(() => store.getters['engagementReport/getIsTableExpanded']);
        const getPreset = computed(() => store.getters['engagementReport/getPreset']);
        const pagination = computed(() => store.getters['engagementReport/getPagination']);
        const limitRange = computed(() => store.getters['engagementReport/getLimitRange']);
        const getDatepickerDates = computed(() => store.getters['dates']);
        const getOrderBy = computed(() => store.getters['engagementReport/getOrderBy']);
        // functions
        const getLabel = (key) => {
            return getColumnsToSearch.value.with_label.filter(column => column.key === key)[0]['label'];
        };
        const tableRowClassName = ({ row, column, columnIndex }) => {
            const threshold = getTableHeader.value[columnIndex]['threshold'];
            const isMetricOrDimension = getTableHeader.value[columnIndex]['isMetric'] ? 'metric' : 'dimension';
            const cellsToPaint = ['SUGGESTIONS_USED_PERC', 'SUGGESTION_CONVERSION_PERC', 'PROACTIVE_WITH_ORDER_PERC'];
            const newClass = `threshold ${isMetricOrDimension} ${getCheckThreshold({
                amount: row[column.property],
                threshold,
            })}`;
            if (cellsToPaint.includes(column.property)) {
                return `${newClass}`;
            }
            return newClass;
        };
        const tableHeaderClassName = ({ columnIndex }) => {
            const isMetric = getTableHeader.value[columnIndex]['isMetric'] ? 'metric' : 'dimension';
            return isMetric;
        };
        const formatter = (row, column) => {
            const typeColumn = getColumnType(column.property);
            return useGetValue({ type: typeColumn, amount: row[column.property] });
        };
        const getColumnType = (property) => {
            return getTableHeader.value.find((col) => col.key === property)?.type || '';
        };
        const getPlaceholder = (column) => {
            return column === 'SUGGESTION_DATE' ? '2000-12-31' : 'Busque nesta coluna';
        };
        const getMask = (column) => {
            return column === 'SUGGESTION_DATE' ? '####-##-##' : noMask.value;
        };
        const clearSearch = (column) => {
            searchTerm.value[column] = '';
            columnsFiltered.value = columnsFiltered.value.filter(col => col !== column);
            clearQuery(column);
            fetchTable();
        };
        const searchByColumn = (column, value) => {
            columnsFiltered.value = [...columnsFiltered.value, column];
            const result = value;
            setQuery({ column, value: result });
            fetchTable();
        };
        const orderTable = ({ prop, order }) => {
            useMixpanelSortedColumn('view_oto_engagement_report_sort-column', prop, order);
            const sortItem = order === 'descending' ? `-${prop}` : prop;
            const hasOrder = !order ? '' : sortItem;
            sort.value.orderBy = !order ? '' : sortItem;
            sort.value.prop = prop;
            sort.value.order = order;
            updateOrderBy(hasOrder);
            fetchTable();
        };
        const getFormattedSummaryRow = ({ columns }) => {
            const summaryRow = getTableSummaryRow.value;
            const totalText = i18n.global.t('_total');
            const generateRow = (fillText) => [totalText, ...new Array(columns.length).fill(fillText)];
            if (!summaryRow)
                return getIsTableLoading.value ? generateRow('-') : generateRow(i18n.global.t('_loading-dots'));
            return columns.map((column, index) => (index === 0 ? totalText : formatter(summaryRow, column)));
        };
        watch(() => getTableBody.value, newValue => {
            tableFiltered.value = newValue;
        });
        watch(() => getPreset.value, () => {
            columnsFiltered.value = [];
            searchTerm.value = {};
        });
        watch(() => getDatepickerDates, () => {
            updateByDatepicker();
        }, { deep: true });
        watch(() => getOrderBy.value, () => {
            if (!getOrderBy.value) {
                sort.value.orderBy = '';
                sort.value.prop = '';
                sort.value.order = '';
            }
        });
        return (_ctx, _cache) => {
            const _component_el_popover = _resolveComponent("el-popover");
            const _component_el_input = _resolveComponent("el-input");
            const _component_el_button = _resolveComponent("el-button");
            const _component_el_table_column = _resolveComponent("el-table-column");
            const _component_el_table = _resolveComponent("el-table");
            const _component_el_dialog = _resolveComponent("el-dialog");
            const _component_base_card_header = _resolveComponent("base-card-header");
            const _component_base_card_v2 = _resolveComponent("base-card-v2");
            const _directive_mask = _resolveDirective("mask");
            return (_openBlock(), _createBlock(_component_base_card_v2, { class: "card engagement-report-table" }, {
                default: _withCtx(() => [
                    _createVNode(_component_el_dialog, {
                        class: "dialog-metrics",
                        modelValue: getIsTableExpanded.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((getIsTableExpanded).value = $event)),
                        fullscreen: "",
                        height: "70vh",
                        width: "100%",
                        onClose: _cache[1] || (_cache[1] = ($event) => (setIsTableExpanded(false)))
                    }, {
                        title: _withCtx(() => [
                            _createElementVNode("div", _hoisted_1, [
                                _hoisted_2,
                                _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.$t('metrics')), 1)
                            ]),
                            _createElementVNode("div", null, [
                                _createElementVNode("div", null, [
                                    _createVNode(EngagementReportTableOptions, { "has-expanded-table": false }),
                                    (!getIsTableLoading.value && !getHasErrorTable.value)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (!getIsTableLoading.value)
                                                ? (_openBlock(), _createBlock(_component_el_table, {
                                                    key: 0,
                                                    data: tableFiltered.value,
                                                    "show-summary": "",
                                                    "summary-method": getFormattedSummaryRow,
                                                    style: { "width": "100%" },
                                                    stripe: "",
                                                    "empty-text": _ctx.$t('is-empty-message'),
                                                    "scrollbar-always-on": true,
                                                    "cell-class-name": tableRowClassName,
                                                    "header-cell-class-name": tableHeaderClassName,
                                                    "default-sort": sort.value,
                                                    onSortChange: orderTable
                                                }, {
                                                    default: _withCtx(() => [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTableHeader.value, (column, index) => {
                                                            return (_openBlock(), _createBlock(_component_el_table_column, {
                                                                key: index,
                                                                formatter: formatter,
                                                                sortable: "custom",
                                                                fixed: index === 0 ?? true,
                                                                prop: column.key,
                                                                label: column.name,
                                                                "min-width": 180
                                                            }, {
                                                                header: _withCtx(() => [
                                                                    (!getColumnsToSearch.value.no_label.includes(column.key))
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                                                            _createElementVNode("label", null, _toDisplayString(column.name), 1),
                                                                            (column.tooltip)
                                                                                ? (_openBlock(), _createBlock(_component_el_popover, {
                                                                                    key: 0,
                                                                                    trigger: "hover",
                                                                                    placement: "top",
                                                                                    width: 180
                                                                                }, {
                                                                                    reference: _withCtx(() => [
                                                                                        _hoisted_5
                                                                                    ]),
                                                                                    default: _withCtx(() => [
                                                                                        _createElementVNode("p", null, _toDisplayString(column.tooltip), 1)
                                                                                    ]),
                                                                                    _: 2
                                                                                }, 1024))
                                                                                : _createCommentVNode("", true)
                                                                        ]))
                                                                        : _createCommentVNode("", true),
                                                                    (getColumnsToSearch.value.no_label.includes(column.key))
                                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                                                            _createElementVNode("label", null, _toDisplayString(getLabel(column.key)), 1),
                                                                            _createVNode(_component_el_popover, {
                                                                                trigger: "hover",
                                                                                placement: "bottom",
                                                                                width: 260
                                                                            }, {
                                                                                reference: _withCtx(() => [
                                                                                    _createElementVNode("i", {
                                                                                        class: _normalizeClass([{ 'color-oto-brand-omni': columnsFiltered.value.includes(column.key) }, "icon icon-search-12px ml--4"])
                                                                                    }, null, 2)
                                                                                ]),
                                                                                default: _withCtx(() => [
                                                                                    _withDirectives(_createVNode(_component_el_input, {
                                                                                        modelValue: searchTerm.value[column.key],
                                                                                        "onUpdate:modelValue": ($event) => ((searchTerm.value[column.key]) = $event),
                                                                                        size: "small",
                                                                                        placeholder: getPlaceholder(column.key),
                                                                                        onKeyup: _withKeys(($event) => (searchByColumn(column.key, searchTerm.value[column.key])), ["enter"])
                                                                                    }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder", "onKeyup"]), [
                                                                                        [_directive_mask, getMask(column.key)]
                                                                                    ]),
                                                                                    _createElementVNode("div", null, [
                                                                                        _createVNode(_component_el_button, {
                                                                                            disabled: !searchTerm.value[column.key],
                                                                                            round: "",
                                                                                            size: "small",
                                                                                            text: "",
                                                                                            onClick: ($event) => (clearSearch(column.key))
                                                                                        }, {
                                                                                            default: _withCtx(() => [
                                                                                                _createTextVNode(_toDisplayString(searchTerm.value[column.key] ? 'limpar filtro' : 'cancelar'), 1)
                                                                                            ]),
                                                                                            _: 2
                                                                                        }, 1032, ["disabled", "onClick"]),
                                                                                        _createVNode(_component_el_button, {
                                                                                            disabled: !searchTerm.value[column.key],
                                                                                            round: "",
                                                                                            size: "small",
                                                                                            type: "primary",
                                                                                            onClick: ($event) => (searchByColumn(column.key, searchTerm.value[column.key]))
                                                                                        }, {
                                                                                            default: _withCtx(() => [
                                                                                                _createTextVNode("pesquisar")
                                                                                            ]),
                                                                                            _: 2
                                                                                        }, 1032, ["disabled", "onClick"])
                                                                                    ])
                                                                                ]),
                                                                                _: 2
                                                                            }, 1024)
                                                                        ]))
                                                                        : _createCommentVNode("", true)
                                                                ]),
                                                                _: 2
                                                            }, 1032, ["fixed", "prop", "label"]));
                                                        }), 128))
                                                    ]),
                                                    _: 1
                                                }, 8, ["data", "empty-text", "default-sort"]))
                                                : _createCommentVNode("", true),
                                            _withDirectives(_createVNode(InfoCard, { error: getHasErrorTable.value }, null, 8, ["error"]), [
                                                [_vShow, !getIsTableLoading.value && getIsEmptyTable.value]
                                            ])
                                        ], 64))
                                        : _createCommentVNode("", true),
                                    _withDirectives(_createVNode(BaseLoader, null, null, 512), [
                                        [_vShow, getIsTableLoading.value]
                                    ]),
                                    _withDirectives(_createVNode(InfoCard, { error: getHasErrorTable.value }, null, 8, ["error"]), [
                                        [_vShow, !getIsTableLoading.value && getHasErrorTable.value]
                                    ]),
                                    (!getIsTableLoading.value && !getHasErrorTable.value)
                                        ? (_openBlock(), _createBlock(ListPagination, {
                                            key: 1,
                                            data: pagination.value,
                                            limit: pagination.value.limit,
                                            "limit-range": limitRange.value,
                                            identify: "engagement-report",
                                            onChangePage: fetchTable
                                        }, null, 8, ["data", "limit", "limit-range"]))
                                        : _createCommentVNode("", true)
                                ])
                            ])
                        ]),
                        _: 1
                    }, 8, ["modelValue"]),
                    _createVNode(_component_base_card_header, {
                        title: _ctx.$t('360.metrics'),
                        icon: "icon-table-30px"
                    }, null, 8, ["title"]),
                    _createVNode(EngagementReportTableOptions, { "has-expanded-table": true }),
                    (!getIsTableLoading.value && !getHasErrorTable.value)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            (!getIsTableLoading.value)
                                ? (_openBlock(), _createBlock(_component_el_table, {
                                    key: 0,
                                    data: tableFiltered.value,
                                    "show-summary": "",
                                    "summary-method": getFormattedSummaryRow,
                                    style: { "width": "100%" },
                                    stripe: "",
                                    "scrollbar-always-on": true,
                                    "cell-class-name": tableRowClassName,
                                    "header-cell-class-name": tableHeaderClassName,
                                    "default-sort": sort.value,
                                    onSortChange: orderTable
                                }, {
                                    default: _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getTableHeader.value, (column, index) => {
                                            return (_openBlock(), _createBlock(_component_el_table_column, {
                                                key: index,
                                                formatter: formatter,
                                                sortable: "custom",
                                                fixed: index === 0 ?? true,
                                                prop: column.key,
                                                label: column.name,
                                                "min-width": 180
                                            }, {
                                                header: _withCtx(() => [
                                                    (!getColumnsToSearch.value.no_label.includes(column.key))
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                                            _createElementVNode("label", null, _toDisplayString(column.name), 1),
                                                            (column.tooltip)
                                                                ? (_openBlock(), _createBlock(_component_el_popover, {
                                                                    key: 0,
                                                                    trigger: "hover",
                                                                    placement: "top",
                                                                    width: 180
                                                                }, {
                                                                    reference: _withCtx(() => [
                                                                        _hoisted_8
                                                                    ]),
                                                                    default: _withCtx(() => [
                                                                        _createElementVNode("p", null, _toDisplayString(column.tooltip), 1)
                                                                    ]),
                                                                    _: 2
                                                                }, 1024))
                                                                : _createCommentVNode("", true)
                                                        ]))
                                                        : _createCommentVNode("", true),
                                                    (getColumnsToSearch.value.no_label.includes(column.key))
                                                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                                            _createElementVNode("label", null, _toDisplayString(getLabel(column.key)), 1),
                                                            _createVNode(_component_el_popover, {
                                                                trigger: "hover",
                                                                placement: "bottom",
                                                                width: 260
                                                            }, {
                                                                reference: _withCtx(() => [
                                                                    _createElementVNode("i", {
                                                                        class: _normalizeClass([{ 'color-oto-brand-omni': columnsFiltered.value.includes(column.key) }, "icon icon-search-12px ml--4"])
                                                                    }, null, 2)
                                                                ]),
                                                                default: _withCtx(() => [
                                                                    _withDirectives(_createVNode(_component_el_input, {
                                                                        modelValue: searchTerm.value[column.key],
                                                                        "onUpdate:modelValue": ($event) => ((searchTerm.value[column.key]) = $event),
                                                                        size: "small",
                                                                        placeholder: getPlaceholder(column.key),
                                                                        onKeyup: _withKeys(($event) => (searchByColumn(column.key, searchTerm.value[column.key])), ["enter"])
                                                                    }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder", "onKeyup"]), [
                                                                        [_directive_mask, getMask(column.key)]
                                                                    ]),
                                                                    _createElementVNode("div", _hoisted_10, [
                                                                        _createVNode(_component_el_button, {
                                                                            disabled: !searchTerm.value[column.key],
                                                                            icon: _unref(Close),
                                                                            size: "small",
                                                                            text: "",
                                                                            onClick: ($event) => (clearSearch(column.key))
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createTextVNode(_toDisplayString(searchTerm.value[column.key] ? 'limpar filtro' : 'cancelar'), 1)
                                                                            ]),
                                                                            _: 2
                                                                        }, 1032, ["disabled", "icon", "onClick"]),
                                                                        _createVNode(_component_el_button, {
                                                                            disabled: !searchTerm.value[column.key],
                                                                            icon: _unref(Search),
                                                                            size: "small",
                                                                            type: "primary",
                                                                            onClick: ($event) => (searchByColumn(column.key, searchTerm.value[column.key]))
                                                                        }, {
                                                                            default: _withCtx(() => [
                                                                                _createTextVNode("pesquisar")
                                                                            ]),
                                                                            _: 2
                                                                        }, 1032, ["disabled", "icon", "onClick"])
                                                                    ])
                                                                ]),
                                                                _: 2
                                                            }, 1024)
                                                        ]))
                                                        : _createCommentVNode("", true)
                                                ]),
                                                _: 2
                                            }, 1032, ["fixed", "prop", "label"]));
                                        }), 128))
                                    ]),
                                    _: 1
                                }, 8, ["data", "default-sort"]))
                                : _createCommentVNode("", true),
                            _withDirectives(_createVNode(InfoCard, { error: getHasErrorTable.value }, null, 8, ["error"]), [
                                [_vShow, !getIsTableLoading.value && getIsEmptyTable.value]
                            ])
                        ], 64))
                        : _createCommentVNode("", true),
                    _withDirectives(_createVNode(BaseLoader, null, null, 512), [
                        [_vShow, getIsTableLoading.value]
                    ]),
                    _withDirectives(_createVNode(InfoCard, { error: getHasErrorTable.value }, null, 8, ["error"]), [
                        [_vShow, !getIsTableLoading.value && getHasErrorTable.value]
                    ]),
                    _createVNode(EngagementReportEditColumns),
                    (!getIsTableLoading.value && !getHasErrorTable.value)
                        ? (_openBlock(), _createBlock(ListPagination, {
                            key: 1,
                            data: pagination.value,
                            limit: pagination.value.limit,
                            "limit-range": limitRange.value,
                            identify: "engagement-report",
                            onChangePage: fetchTable,
                            "show-list-pages": false,
                            "show-quantity-lines": true
                        }, null, 8, ["data", "limit", "limit-range"]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }));
        };
    }
});
